import { z } from 'zod';

/**
 * Гарантийный период после сдачи объекта (ДГ-22): обращения заказчика со сроками реакции и устранения,
 * встречная претензия поставщику или подрядчику. Оценки от 1 до 5 (ДГ-27, КА-15): заказчик оценивает нашу работу,
 * мы — поставщиков и подрядчиков; низкая оценка сразу уходит руководителю проекта.
 */

export const WARRANTY_STATUSES = {
  new: 'Новое',
  in_work: 'В работе',
  resolved: 'Устранено',
  rejected: 'Не гарантийный случай',
} as const;
export type WarrantyStatus = keyof typeof WARRANTY_STATUSES;

export const RATING_KINDS = {
  customer: 'Заказчик оценивает нашу работу',
  supplier: 'Оценка поставщика',
  contractor: 'Оценка подрядчика',
} as const;
export type RatingKind = keyof typeof RATING_KINDS;
export const RATING_KIND_CODES = Object.keys(RATING_KINDS) as [RatingKind, ...RatingKind[]];

export const RATING_SOURCES = {
  manual: 'Без привязки',
  claim: 'Гарантийное обращение',
  receipt: 'Поступление на склад',
  stage: 'Этап работ',
} as const;
export type RatingSource = keyof typeof RATING_SOURCES;

/** Умолчания; у компании свои — в настройках (`warranty.terms`) */
export const WARRANTY_DEFAULTS = { reactionDays: 2, fixDays: 14, alertScore: 3 } as const;
export const WARRANTY_SETTINGS_KEY = 'warranty.terms';
/** Окно «Ждёт меня»: сколько дней до срока обращение уже показывается, дней */
export const WARRANTY_HOME_DAYS = 3;

const Day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (d) =>
      Number.isFinite(Date.parse(`${d}T00:00:00Z`)) && new Date(`${d}T00:00:00Z`).toISOString().startsWith(d),
    'Неверная дата',
  );

export const WarrantySettings = z.object({
  reactionDays: z.number().int().min(1).max(60),
  fixDays: z.number().int().min(1).max(365),
  /** Оценка не выше этой уходит руководителю сразу */
  alertScore: z.number().int().min(1).max(4),
});
export type WarrantySettings = z.infer<typeof WarrantySettings>;

export const WarrantyClaimInput = z.object({
  title: z.string().trim().min(3).max(300),
  description: z.string().trim().max(4000).nullish(),
  reporterName: z.string().trim().max(200).nullish(),
  reportedOn: Day.optional(),
  /** Срок устранения, согласованный с заказчиком; без него — по настройке компании */
  fixDueOn: Day.nullish(),
  assigneeId: z.uuid().nullish(),
});
export type WarrantyClaimInput = z.infer<typeof WarrantyClaimInput>;

export const WarrantyTake = z.object({ assigneeId: z.uuid().nullish() });
export const WarrantyResolve = z.object({
  resolution: z.string().trim().min(3).max(4000),
  costAmount: z.number().nonnegative().max(1e12).nullish(),
});
export const WarrantyReject = z.object({ reason: z.string().trim().min(3).max(2000) });
export const WarrantyCounter = z.object({
  counterContractId: z.uuid(),
  counterNote: z.string().trim().max(2000).nullish(),
});

export const RatingInput = z.object({
  kind: z.enum(RATING_KIND_CODES),
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().max(2000).nullish(),
  /** Кто оценил со стороны заказчика */
  raterName: z.string().trim().max(200).nullish(),
  sourceType: z.enum(['manual', 'claim', 'receipt', 'stage']).default('manual'),
  sourceId: z.uuid().nullish(),
  ratedOn: Day.optional(),
});
export type RatingInput = z.infer<typeof RatingInput>;

export const WarrantyClaimRow = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  customerName: z.string(),
  number: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  reporterName: z.string().nullable(),
  reportedOn: z.string(),
  reactionDueOn: z.string(),
  fixDueOn: z.string(),
  respondedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  status: z.string(),
  assigneeId: z.uuid().nullable(),
  assigneeName: z.string().nullable(),
  resolution: z.string().nullable(),
  /** Расходы на устранение; без уровня денег «Акты и счета» скрыты */
  costAmount: z.number().nullable(),
  counterContractId: z.uuid().nullable(),
  counterContractNumber: z.string().nullable(),
  counterCounterpartyName: z.string().nullable(),
  counterNote: z.string().nullable(),
  /** До какой даты у встречного договора действует гарантия или гарантийный срок */
  counterWarrantyUntil: z.string().nullable(),
  reactionLate: z.boolean(),
  fixLate: z.boolean(),
  /** Дней до срока устранения; отрицательное — просрочено; у закрытого — null */
  fixDaysLeft: z.number().nullable(),
  /** Обращение позже окончания гарантийного срока объекта */
  outOfWarranty: z.boolean(),
  rated: z.boolean(),
});
export type WarrantyClaimRow = z.infer<typeof WarrantyClaimRow>;

export const RatingRow = z.object({
  id: z.uuid(),
  contractId: z.uuid(),
  contractNumber: z.string(),
  projectId: z.uuid(),
  projectCode: z.string(),
  counterpartyId: z.uuid(),
  counterpartyName: z.string(),
  kind: z.string(),
  score: z.number(),
  comment: z.string().nullable(),
  raterName: z.string().nullable(),
  sourceType: z.string(),
  sourceId: z.uuid().nullable(),
  sourceLabel: z.string().nullable(),
  ratedOn: z.string(),
  createdByName: z.string().nullable(),
  low: z.boolean(),
});
export type RatingRow = z.infer<typeof RatingRow>;

export const RatingSummary = z.object({
  count: z.number(),
  average: z.number().nullable(),
  low: z.number(),
});
export type RatingSummary = z.infer<typeof RatingSummary>;

export const ContractWarranty = z.object({
  claims: z.array(WarrantyClaimRow),
  ratings: z.array(RatingRow),
  /** Средняя оценка контрагента по всем договорам компании */
  counterpartyRating: RatingSummary,
  /** Поступления на склад по договору, которые вы ещё не оценили */
  receiptsToRate: z.array(z.object({ id: z.uuid(), label: z.string(), docDate: z.string() })),
  warrantyUntil: z.string().nullable(),
  settings: WarrantySettings,
  moneyHidden: z.boolean(),
  canEdit: z.boolean(),
  /** Что можно оценить на этом договоре */
  ratingKinds: z.array(z.enum(RATING_KIND_CODES)),
  people: z.array(z.object({ id: z.uuid(), fullName: z.string() })),
  /** Расходные договоры проекта для встречной претензии */
  counterContracts: z.array(z.object({ id: z.uuid(), label: z.string() })),
});
export type ContractWarranty = z.infer<typeof ContractWarranty>;

export const WarrantyOverview = z.object({
  claims: z.array(WarrantyClaimRow),
  open: z.number(),
  late: z.number(),
  lowRatings: z.array(RatingRow),
  moneyHidden: z.boolean(),
});
export type WarrantyOverview = z.infer<typeof WarrantyOverview>;
